import dayjs from 'dayjs';
import { defineMessages, IntlShape } from 'react-intl';

import { AlertLog, BaselineAlert, ClosureReasons } from 'src/types/alerts';
import { createStringFromTemplate } from './general';
import { ALERT_METRIC_ENUM } from 'src/redux/data/constants';
import {
  getLongOutOfBedSuffix,
  getLongOutOfBedTriggerValue,
} from 'src/utils/alertHelpers';
import { TIME_24H_FORMAT } from '../timeUtils';
import {
  getFormattedLocale,
  getLocaleFromUrlPath,
  supportedLocales,
} from 'src/utils/languageUtils';

export const messages = defineMessages({
  high: {
    id: 'app.greeting',
    defaultMessage: 'High',
  },
  low: {
    id: 'messages.alertMessages.low',
    defaultMessage: 'Low',
  },
  bpm: {
    id: 'messages.alertMessages.bpm',
    defaultMessage: 'BPM',
  },
  brpm: {
    id: 'messages.alertMessages.brpm',
    defaultMessage: 'BrPM',
  },
  hr: {
    id: 'messages.alertMessages.hr',
    defaultMessage: 'HR',
  },
  rr: {
    id: 'messages.alertMessages.rr',
    defaultMessage: 'RR',
  },
  alert: {
    id: 'messages.alertMessages.alert',
    defaultMessage: 'Notification',
  },
  anAverageOf: {
    id: 'messages.alertMessages.anAverageOf',
    defaultMessage: 'An average of',
  },
  hours: {
    id: 'messages.alertMessages.hours',
    defaultMessage: 'hours',
  },
  by: {
    id: 'messages.alertMessages.by',
    defaultMessage: 'by',
  },
  orMore: {
    id: 'messages.alertMessages.orMore',
    defaultMessage: 'or more',
  },
  since: {
    id: 'messages.alertMessages.since',
    defaultMessage: 'since',
  },
  deviatesFromBaseline: {
    id: 'messages.alertMessages.deviatesFromBaseline',
    defaultMessage: 'deviates from',
  },
  partial: {
    id: 'messages.alertMessages.partial',
    defaultMessage: 'partial',
  },
  baseline: {
    id: 'messages.alertMessages.baseline',
    defaultMessage: 'baseline',
  },
  days: {
    id: 'messages.alertMessages.days',
    defaultMessage: 'days',
  },
  bedExitDetails: {
    id: 'messages.alertMessages.bedExitDetails',
    defaultMessage: 'BedExit Since',
  },
  unoccupied: {
    id: 'messages.alertMessages.unoccupied',
    defaultMessage: 'Unoccupied',
  },
  hour: {
    id: 'messages.alertMessages.hour',
    defaultMessage: 'hour',
  },
  minute: {
    id: 'messages.alertMessages.minute',
    defaultMessage: 'minute',
  },
  minutes: {
    id: 'messages.alertMessages.minutes',
    defaultMessage: 'minutes',
  },
  second: {
    id: 'messages.alertMessages.second',
    defaultMessage: 'second',
  },
  seconds: {
    id: 'messages.alertMessages.seconds',
    defaultMessage: 'seconds',
  },
  for: {
    id: 'messages.alertMessages.for',
    defaultMessage: 'for',
  },
  is: {
    id: 'messages.alertMessages.is',
    defaultMessage: 'is',
  },
  bedTimeHoursDiffer: {
    id: 'messages.alertMessages.bedTimeHoursDiffer',
    defaultMessage: 'Bed-Time hours differs from last',
  },
  bedExitFrequencyAlertDetails: {
    id: 'messages.alertMessages.bedExitFrequencyAlertDetails',
    defaultMessage:
      'BedExit Frequency Notification: {bedExitsCount} times during the pre-defined period',
  },
  positionChangeAlertDetails: {
    id: 'messages.alertMessages.positionChangeAlertDetails',
    defaultMessage: 'Patient position has not been changed',
  },
  on: {
    id: 'messages.alertMessages.on',
    defaultMessage: 'on',
  },
  and: {
    id: 'messages.alertMessages.and',
    defaultMessage: 'and',
  },
  patient: {
    id: 'messages.alertMessages.patient',
    defaultMessage: 'Patient is out of bed for',
  },
  noConsentAlertLogDetails: {
    id: 'messages.alertMessages.noConsentAlertLogDetails',
    defaultMessage:
      '{strongStart}Status changed due to No Consent Status {lineBreak}{strongEnd}',
  },
  above: {
    id: 'messages.alertMessages.above',
    defaultMessage: 'above',
  },
  below: {
    id: 'messages.alertMessages.below',
    defaultMessage: 'below',
  },
});

const calculateSinceTime = (
  averageIntervalInSec: number,
  startTime: string,
): string =>
  dayjs(startTime)
    .subtract(averageIntervalInSec, 'second')
    .format(TIME_24H_FORMAT);

export const getBaselineAlertDetails = (
  alert: Pick<
    BaselineAlert,
    | 'thresholdMetric'
    | 'thresholdPreposition'
    | 'deviationHours'
    | 'deviationValue'
    | 'baselineValue'
    | 'baselineDaysInterval'
    | 'deviationPercentage'
    | 'reminderCount'
    | 'startTime'
    | 'actualBaselineIntervalHours'
  >,
  intl: IntlShape,
): string => {
  const {
    thresholdMetric,
    thresholdPreposition,
    deviationHours,
    deviationValue,
    baselineValue,
    baselineDaysInterval,
    deviationPercentage,
    reminderCount,
    startTime,
    actualBaselineIntervalHours,
  } = alert;
  const language = getFormattedLocale(
    getLocaleFromUrlPath(window.location.pathname),
  );

  const metricMessage =
    thresholdMetric === ALERT_METRIC_ENUM.HR_BASELINE
      ? messages.hr
      : messages.rr;
  const unitMessage =
    thresholdMetric === ALERT_METRIC_ENUM.HR_BASELINE
      ? messages.bpm
      : messages.brpm;

  const reminderCountText = reminderCount
    ? `| ${intl.formatMessage(messages.alert)} #${reminderCount}`
    : '';

  const sinceTime = deviationHours
    ? calculateSinceTime(Math.round(deviationHours * 60 * 60), startTime)
    : '';

  const isValidSinceTime = sinceTime !== 'Invalid Date';

  const stringTemplate =
    language === supportedLocales.fr_FR
      ? `{metric} {prepositionIndicator} - {anAverageOf} {deviationHours} {hours} ({deviationValue} {unit}) {deviatesFromBaseline} {partialBaseline} {baselineDaysInterval} {days} {baseline} ({baselineValue} {unit}) {by} {deviationPercentage}% {orMore} {since} {sinceTime} {reminderCountText}`
      : `{prepositionIndicator} {metric} - {anAverageOf} {deviationHours} {hours} ({deviationValue} {unit}) {deviatesFromBaseline} {partialBaseline} {baselineDaysInterval} {days} {baseline} ({baselineValue} {unit}) {by} {deviationPercentage}% {orMore} {since} {sinceTime} {reminderCountText}`;

  const details = createStringFromTemplate(stringTemplate, {
    prepositionIndicator: intl.formatMessage(
      thresholdPreposition === 'ABOVE' ? messages.high : messages.low,
    ),
    metric: intl.formatMessage(metricMessage),
    anAverageOf: intl.formatMessage(messages.anAverageOf),
    hours:
      deviationHours === 1
        ? intl.formatMessage(messages.hour)
        : intl.formatMessage(messages.hours),
    deviationHours: deviationHours?.toString() || '',
    deviatesFromBaseline: intl.formatMessage(messages.deviatesFromBaseline),
    deviationValue: deviationValue ? Math.round(deviationValue).toString() : '',
    partialBaseline: actualBaselineIntervalHours
      ? intl.formatMessage(messages.partial)
      : '',
    unit: intl.formatMessage(unitMessage),
    by: intl.formatMessage(messages.by),
    baseline: intl.formatMessage(messages.baseline),
    days: intl.formatMessage(messages.days),
    baselineValue: baselineValue ? Math.round(baselineValue).toString() : '',
    baselineDaysInterval: baselineDaysInterval
      ? Math.round(baselineDaysInterval).toString()
      : '',
    deviationPercentage: deviationPercentage
      ? Math.floor(Math.abs(deviationPercentage)).toString()
      : '',
    orMore: intl.formatMessage(messages.orMore),
    since: isValidSinceTime ? intl.formatMessage(messages.since) : '',
    sinceTime:
      isValidSinceTime && deviationHours
        ? calculateSinceTime(Math.round(deviationHours * 60 * 60), startTime)
        : '',
    reminderCountText: reminderCountText,
  });

  return details;
};

export const getAlertLogDetails = (
  alert: AlertLog,
  intl: IntlShape,
): string => {
  const {
    triggerValue,
    thresholdMetric,
    thresholdPreposition,
    averageInterval,
    thresholdValue,
    averageValue,
    startTime,
    firstMeasurementTime,
    closureReason,
  } = alert;
  const formatDuration = (duration: number): string => {
    if (duration < 60) {
      return `${duration} ${duration === 1 ? intl.formatMessage(messages.second) : intl.formatMessage(messages.seconds)}`;
    }

    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;

    return `${minutes}${
      seconds ? `:${seconds < 10 ? `0${seconds}` : seconds}` : ''
    } ${minutes > 1 ? intl.formatMessage(messages.minutes) : intl.formatMessage(messages.minute)}`;
  };

  const detailsPrefix =
    closureReason === ClosureReasons.PATIENT_CONSENT_DISABLED
      ? intl.formatMessage(messages.noConsentAlertLogDetails, {
          lineBreak: '<br />',
          strongStart: '<strong>',
          strongEnd: '</strong>',
        })
      : '';
  if (!thresholdMetric || thresholdMetric === ALERT_METRIC_ENUM.BED_EXIT) {
    return `${detailsPrefix}${intl.formatMessage(messages.bedExitDetails)} ${dayjs(
      startTime,
    ).format(TIME_24H_FORMAT)}`;
  }

  if (
    !thresholdMetric ||
    thresholdMetric === ALERT_METRIC_ENUM.LONG_OUT_OF_BED
  ) {
    const longOutOfBedTriggerValue = getLongOutOfBedTriggerValue(triggerValue);
    const longOutOfBedTriggerValueSuffix = getLongOutOfBedSuffix(triggerValue);

    return `${detailsPrefix}${intl.formatMessage(messages.patient)}
     ${longOutOfBedTriggerValue}
      ${intl.formatMessage(longOutOfBedTriggerValueSuffix)}
       ${intl.formatMessage(messages.orMore)} ${intl.formatMessage(messages.since)}
        ${dayjs(firstMeasurementTime).format(TIME_24H_FORMAT)}`;
  }

  if (
    !thresholdMetric ||
    thresholdMetric === ALERT_METRIC_ENUM.BED_EXIT_FREQUENCY
  ) {
    return `${detailsPrefix}${intl.formatMessage(messages.bedExitFrequencyAlertDetails, { bedExitsCount: triggerValue })}`;
  }

  if (
    thresholdMetric === ALERT_METRIC_ENUM.HR_BASELINE ||
    thresholdMetric === ALERT_METRIC_ENUM.RR_BASELINE
  ) {
    return `${detailsPrefix}${getBaselineAlertDetails(alert, intl)}`;
  }

  if (thresholdMetric === ALERT_METRIC_ENUM.POSITION_CHANGE) {
    return `${detailsPrefix}${intl.formatMessage(messages.positionChangeAlertDetails)}`;
  }

  if (!thresholdPreposition || !thresholdValue) {
    return `${detailsPrefix}`;
  }

  if (
    thresholdMetric === ALERT_METRIC_ENUM.HR ||
    thresholdMetric === ALERT_METRIC_ENUM.RR
  ) {
    return `${detailsPrefix}${thresholdMetric} - ${intl.formatMessage(messages.anAverageOf)} ${formatDuration(
      averageInterval,
    )} ${intl.formatMessage(messages.is)} ${intl.formatMessage(
      thresholdPreposition === 'ABOVE' ? messages.above : messages.below,
    )} ${thresholdValue} ${intl.formatMessage(
      thresholdMetric === ALERT_METRIC_ENUM.HR ? messages.bpm : messages.brpm,
    )} (${Math.round(averageValue)}) 
      ${intl.formatMessage(messages.since)} ${calculateSinceTime(averageInterval, startTime)}`;
  }

  return `${detailsPrefix}`;
};
